import { Box } from '@chakra-ui/react';
import { forwardRef, useImperativeHandle, useRef, useCallback } from 'react';
import { useAuth, useShowToast } from '@/hooks';
import { FormBuilder } from '@/components/formBuilder';
import { useEmailField } from '@/components/formBuilder/formFields/useEmailField';
import { useKFlags } from '@/hooks/use-k-flags';
import { useSelector } from 'react-redux';
import { selectUsers } from '@/store/users/selectors';
import { useGetAllUsers } from '@/hooks/useGetAllUsers';
import { getEmailFormData } from '@/utils/getEmailFormConfig';

export interface OfferEmailFormRef {
  getModel: () => Promise<{
    hasValidData: boolean;
    payload: any;
  }>;
}

export interface OfferEmailFormProps {
  selectedTemplate: any;
  attachments: any[];
  variables: any[];
  onAttachmentRemove?: (file: any) => void;
}

export const OfferEmailForm = forwardRef<
  OfferEmailFormRef,
  OfferEmailFormProps
>(
  (
    { selectedTemplate, attachments, variables, onAttachmentRemove },
    comRef
  ) => {
    const { currUser } = useAuth();
    const toast = useShowToast();
    const createFormRef = useRef<any>(null);

    const { feSharedEmails } = useKFlags();
    const users = useSelector(selectUsers) as any[];
    const { processFromUserPayload } = useGetAllUsers();

    const emailFieldConfigs = useEmailField({
      formRef: createFormRef,
      userEmailType: 'hiringTeam',
      templateData: selectedTemplate,
      variables,
      onAttachmentRemove,
      enableFontSize: true,
      enableFontFamily: true,
    });

    useImperativeHandle(comRef, () => ({
      getModel: async () => {
        if (!createFormRef.current) {
          return {
            hasValidData: false,
            payload: {},
          };
        }

        const form = createFormRef.current;
        const { errors, values } = form || {};

        // Check for any form validation errors
        const [firstErrorField, errorMessage] =
          Object.entries(errors || {})[0] || [];
        if (firstErrorField) {
          toast({
            title: errorMessage,
            status: 'error',
          });
          return {
            hasValidData: false,
            payload: {},
          };
        }

        const { body, from, subject, fromUserObject } = values || {};
        if (!from && !fromUserObject) {
          toast({ title: `From is mandatory`, status: 'error' });
          return { hasValidData: false, payload: {} };
        } else if (!subject) {
          toast({ title: `Subject is mandatory`, status: 'error' });
          return { hasValidData: false, payload: {} };
        } else if (!body) {
          toast({ title: `Body is mandatory`, status: 'error' });
          return { hasValidData: false, payload: {} };
        }

        let fromPayload: any = {
          senderId: undefined,
          ats_shared_email_id: undefined,
        };

        if (feSharedEmails) {
          fromPayload = processFromUserPayload({
            selectedUser: fromUserObject,
            payloadKey: 'senderId',
          });
        } else {
          const senderId = users?.find((u: any) => u?.email === from)?.id;
          fromPayload = { senderId };
        }

        const formattedPayload = getEmailFormData({
          values: {
            ...values,
            selectedTemplate,
          },
          senderId: fromPayload?.senderId,
          ats_shared_email_id: fromPayload?.ats_shared_email_id,
          templateReferenceKey: 'template_id',
          ccKey: 'cc_emails[]',
          bccKey: 'bcc_emails[]',
        });

        return {
          hasValidData: true,
          payload: formattedPayload,
        };
      },
    }));

    const handleFormSubmit = useCallback(() => {
      // Form submission handled by getModel
    }, []);

    const emailFormConfig = [
      {
        id: 'emailFormConfig',
        type: 'wrapper',
        fields: emailFieldConfigs,
        Wrapper: Box,
        wrapperProps: {
          border: '1px solid',
          borderColor: 'border-color',
          borderRadius: 'sm',
        },
      },
    ];

    const fromUserObject = {
      id: currUser?.id,
      email: currUser?.email,
      name: `${currUser?.first_name ?? ''} ${currUser?.last_name ?? ''}`.trim(),
      avatar: currUser?.avatar,
    };

    return (
      <Box w="100%">
        <FormBuilder
          initialValues={{
            from: currUser?.email,
            cc: [],
            bcc: [],
            subject:
              selectedTemplate?.subject ||
              'Congratulations! Your Offer for {{job.title}}',
            body:
              selectedTemplate?.body ||
              `<p dir="auto">Hi {{candidate.first_name}},</p><p dir="auto"></p><p dir="auto">Thank you for your interest in the {{job.title}} position at {{org.name}}.</p><p dir="auto"></p><p dir="auto">We enjoyed getting to know you throughout the interview process.</p><p dir="auto"></p><p dir="auto">We're excited to offer you the {{job.title}} position! We believe you'll be a great fit for our team.</p><p dir="auto"></p><p dir="auto">Please review the attached offer letter for details. Let us know if you have any questions or need any additional information.</p><p dir="auto"></p><p dir="auto">Looking forward to having you on board!</p><p dir="auto"></p><p dir="auto">Best regards,</p><p dir="auto">{{sender.first_name}}</p>`,
            attachments: [
              ...attachments,
              ...(selectedTemplate?.attachments || []),
            ],
            ...(feSharedEmails ? { fromUserObject } : {}),
          }}
          config={emailFormConfig}
          onSubmit={handleFormSubmit}
          formRef={createFormRef}
        />
      </Box>
    );
  }
);

OfferEmailForm.displayName = 'OfferEmailForm';
